/**
 * backfill — populate `trade.barAnalysis` across a trade history in bulk.
 *
 * Until now, `barAnalysis` was only computed when the user opened a trade's
 * chart in the AddEditTradeModal. That meant the Coach's bar-context
 * observations (winner-reversed, MFE-leakage, EMA-stack-against, etc.) only
 * fired on days where the user happened to open every trade — useless for
 * historical analysis.
 *
 * This module walks the trade list, fetches bars per (symbol, date, tf),
 * computes barAnalysis, and returns an updated trades array. Designed to:
 *
 *   - Be called from a button in Settings → Charts.
 *   - Skip trades that already have barAnalysis (re-running is cheap; user
 *     can clear barAnalysis on a trade if they want forced recompute).
 *   - Skip trades missing required inputs (no symbol, no entry time, etc.)
 *     instead of failing the whole batch.
 *   - Cooperate with cancellation via an AbortSignal.
 *   - Emit progress via a callback so the UI can show "X/Y trades processed".
 *
 * Pure logic + injected fetcher
 * ------------------------------
 * `fetchBars` is passed in by the caller (typically `barDataService.fetchBars`
 * bound to the singleton). Keeping the dependency injected means this module
 * is testable with a synchronous mock — no Electron, no IndexedDB.
 *
 * Backfilling broker-style mae/mfe (dollars)
 * -------------------------------------------
 * Same convention as the modal's auto-write: only populate `trade.mae` /
 * `trade.mfe` when they're empty. Broker imports stay authoritative.
 */

import { computeMaeMfe } from './maeMfe.js';
import { detectInTradeCounterSignals } from './counterSignals.js';

// Fields that ONLY exist on barAnalysis blobs computed by Wave A/B or later.
// When a backfill is run with `requireFields: 'wave-a-b'`, trades that have
// barAnalysis but are missing any of these fields get re-analyzed so the
// new analyzers (trendContextScore, entryDiscipline) have data to operate on.
const WAVE_AB_FIELDS = ['trendContextScore', 'entryDiscipline'];

// Wave C added setupPatterns. Use 'wave-c' or the combined 'latest' to
// refresh trades missing the Wave C field too.
const WAVE_C_FIELDS = ['setupPatterns'];
// Wave D added avwapAtEntry. 'wave-d' or 'latest' refresh trades missing
// the AVWAP snapshot field.
const WAVE_D_FIELDS = ['avwapAtEntry'];
// Wave E.2 added keyLevels (Golden Crayon + prior-day OHLC + session H/L
// + today's open). 'wave-e' or 'latest' refresh trades missing it.
const WAVE_E_FIELDS = ['keyLevels'];
// Top-Down Phase A added multiTf — per-TF state vector across D/4H/1H/15M/5M
// (stage, EMA stack + slopes, always-in, trend integrity, RSI state, volume
// regime). 'top-down-mtf' or 'latest' refresh trades missing the field.
// See docs/research/Coach_TopDown_Analysis.md.
const TOP_DOWN_MTF_FIELDS = ['multiTf'];
// Top-Down Phase B added initialBalance — IB high/low/mid + entry-bar
// breakout/reentry classification (port of OpenRangeOptimized).
// 'top-down-ib' or 'latest' refresh trades missing the field.
const TOP_DOWN_IB_FIELDS = ['initialBalance'];
// Top-Down Phase B.5 added avwapConfluenceZones — multi-AVWAP convergence
// detection (Shannon's confluence-zone framework). Closes v7.4.3 audit §3.2.
// 'top-down-avwap-zones' or 'latest' refresh trades missing the field.
const TOP_DOWN_AVWAP_ZONES_FIELDS = ['avwapConfluenceZones'];
// Top-Down Phase B.3 added fourHourBodySR — 4H body-close S/R clusters,
// the chart-visible "4H S/R (Body)" level. 'top-down-4h-sr' or 'latest'.
const TOP_DOWN_4H_SR_FIELDS = ['fourHourBodySR'];
// Top-Down Phase B.4 added volumeProfile — POC / VAH / VAL / HVN / LVN
// (futures-native volume-weighted distribution). 'top-down-vp' or 'latest'.
const TOP_DOWN_VP_FIELDS = ['volumeProfile'];
const LATEST_FIELDS = [
  ...WAVE_AB_FIELDS,
  ...WAVE_C_FIELDS,
  ...WAVE_D_FIELDS,
  ...WAVE_E_FIELDS,
  ...TOP_DOWN_MTF_FIELDS,
  ...TOP_DOWN_IB_FIELDS,
  ...TOP_DOWN_AVWAP_ZONES_FIELDS,
  ...TOP_DOWN_4H_SR_FIELDS,
  ...TOP_DOWN_VP_FIELDS,
];

/**
 * Count trades that would be re-analyzed by `requireFields: 'latest'`.
 * Used by the top-bar "Needs Backfill" pill to surface stale data without
 * making the user open Settings. Mirrors the eligibility predicate used
 * inside backfillBarAnalysis — keep these in sync.
 *
 * Counts trades that:
 *   - Are eligible at all (have symbol, date, entryTime, positive entry)
 *   - Have NO barAnalysis OR are missing any LATEST_FIELDS field
 *   - Have NOT been previously attempted-but-skipped (no `_barAnalysisAttempt`
 *     marker). Both skip reasons are excluded:
 *       - `'no-bars'`: bars don't exist for the trade's date, unlikely to
 *         appear from a re-run. The user can use Force retry skipped after
 *         dropping historical CSVs in.
 *       - `'analysis-failed'`: bars existed but computeMaeMfe threw. Retrying
 *         the same buggy computation won't help until either the analyzer
 *         code changes or the underlying data issue is fixed. Surface via
 *         console.error inside backfillBarAnalysis instead.
 */
export const countTradesNeedingBackfill = (trades) => {
  if (!Array.isArray(trades)) return 0;
  let n = 0;
  for (const trade of trades) {
    if (!trade) continue;
    if (!trade.symbol || !trade.date || !trade.entryTime) continue;
    const entryPx = parseFloat(trade.entry);
    if (!Number.isFinite(entryPx) || entryPx <= 0) continue;
    // Exclude trades we already tried and that couldn't be processed.
    // The pill is meant to nag about ACTIONABLE work — trades whose
    // bars don't exist or whose analyzer is buggy aren't actionable
    // by clicking the button again. They'll come back to the pill
    // naturally once the user adds bar data (Force retry) or we ship
    // an analyzer fix (analyzer-version bump can invalidate markers).
    if (trade._barAnalysisAttempt?.skippedReason === 'no-bars') continue;
    if (trade._barAnalysisAttempt?.skippedReason === 'analysis-failed') continue;
    const ba = trade.barAnalysis;
    if (!ba || typeof ba !== 'object') { n++; continue; }
    if (LATEST_FIELDS.some(f => ba[f] === undefined)) n++;
  }
  return n;
};

/**
 * Decide whether a trade is eligible for backfill.
 *
 * Modes:
 *   - 'missing' (default): only trades with NO barAnalysis. The original
 *     behavior — useful for first-time backfill on a new install.
 *   - 'wave-a-b': also re-analyzes trades whose barAnalysis lacks the
 *     Wave A/B fields (trendContextScore, entryDiscipline). Used after
 *     upgrading from v7.4.3 → v7.4.4.
 *   - 'wave-c': re-analyzes trades whose barAnalysis lacks the Wave C
 *     field (setupPatterns). Used after upgrading to v7.4.5+.
 *   - 'wave-d': re-analyzes trades whose barAnalysis lacks the Wave D
 *     field (avwapAtEntry). Used after upgrading to v7.5.1+.
 *   - 'latest': re-analyzes any trade missing ANY field added in any
 *     wave since v7.4.3 — the catch-all for upgrades. Recommended.
 *   - 'force': re-analyzes EVERY eligible trade regardless of state.
 *     Use sparingly — full re-analysis is expensive on large histories.
 */
const isEligible = (trade, requireFields = 'missing') => {
  if (!trade) return false;
  if (!trade.symbol || !trade.date || !trade.entryTime) return false;
  const entryPx = parseFloat(trade.entry);
  if (!Number.isFinite(entryPx) || entryPx <= 0) return false;

  // Trades we already tried and that had no bars are skipped on default
  // runs — the user can still force them via requireFields='force'.
  // Without this guard, "Run backfill" would re-attempt every historical
  // weekend/holiday trade every single time, wasting fetches and making
  // the user think nothing was saved.
  if (requireFields !== 'force' && trade._barAnalysisAttempt?.skippedReason === 'no-bars') {
    return false;
  }

  const hasBarAnalysis = trade.barAnalysis && typeof trade.barAnalysis === 'object';
  if (!hasBarAnalysis) return true;  // missing → always eligible
  if (requireFields === 'force') return true;
  if (requireFields === 'wave-a-b') {
    return WAVE_AB_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'wave-c') {
    return WAVE_C_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'wave-d') {
    return WAVE_D_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'wave-e') {
    return WAVE_E_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'top-down-mtf') {
    return TOP_DOWN_MTF_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'top-down-ib') {
    return TOP_DOWN_IB_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'top-down-avwap-zones') {
    return TOP_DOWN_AVWAP_ZONES_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'top-down-4h-sr') {
    return TOP_DOWN_4H_SR_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'top-down-vp') {
    return TOP_DOWN_VP_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  if (requireFields === 'latest') {
    return LATEST_FIELDS.some(f => trade.barAnalysis[f] === undefined);
  }
  return false;  // 'missing' default + barAnalysis present → skip
};

/**
 * Run the backfill.
 *
 * @param {Object}   args
 * @param {Array}    args.trades       Full trades array (returned as a new array; original not mutated)
 * @param {Function} args.fetchBars    ({symbol, dateStr, timeframe}) → Promise<{bars: Bar[], source, fromCache}>
 * @param {string}   [args.timeframe]  DEPRECATED for analysis purposes — backfill
 *                                     now ALWAYS uses 1m bars for the MAE/MFE walk
 *                                     so single-bar trades (e.g. 3-minute stop-outs
 *                                     entirely inside one 5m bar) get accurate
 *                                     measurement. The arg is retained for callers
 *                                     that still pass it but is otherwise ignored.
 * @param {number[]} [args.emaPeriods] User's configured EMA periods. Forwarded
 *                                     to computeMaeMfe so the snapshot matches
 *                                     what the chart shows. Omit to use the
 *                                     standard 5/9/21/50/200 defaults.
 * @param {string[]} [args.htfTimeframes]  Higher timeframes to capture stack
 *                                     context for. Defaults to ['15m','1h','4h']
 *                                     which match the trader workflow of
 *                                     "4H/1H/15M structure → 5M entry".
 *                                     Pass [] to skip HTF capture entirely.
 * @param {Function} [args.onProgress] (state) => void — called after each trade processed.
 *                                     state: { processed, total, computed, skipped, lastTradeId }
 * @param {AbortSignal} [args.signal]  Optional — abort partway through.
 * @param {string}   [args.requireFields]  Eligibility mode (see isEligible):
 *                                     'missing' (default) → only trades with no barAnalysis
 *                                     'wave-a-b' → also re-analyze trades missing
 *                                       trendContextScore or entryDiscipline (post-v7.4.4 upgrade)
 *                                     'force' → re-analyze every eligible trade
 * @returns {Promise<{ trades: Array, processed: number, computed: number, skipped: number, aborted: boolean }>}
 *          `trades` is the updated array. `computed` = trades for which we
 *          successfully wrote barAnalysis. `skipped` = trades with no usable
 *          bars or that errored. Caller persists via setTrades().
 */
// Default chunk size for parallel processing. 8 balances:
//   - I/O parallelism: 8 concurrent file reads benefits from OS cache + lets
//     event loop service other work while any one read awaits disk
//   - CPU contention: computeMaeMfe is CPU-bound and single-threaded; too
//     much parallelism just queues work behind the event loop and doesn't
//     improve wall time
//   - File-system pressure: backfill reads the same CSVs many times for
//     trades on adjacent dates — OS file cache amplifies this, so 8 reads
//     to the same hot file is essentially free
// Empirically: 400 trades × ~50-200ms serial = ~1-3min → chunked-8 = ~10-20s.
const DEFAULT_CHUNK_SIZE = 8;

/**
 * Process one trade end-to-end. Pure async function (no shared state writes).
 * Returns an intent describing what should be merged into the trades array
 * once the chunk's Promise.all settles. Keeping the merge OUT of this
 * function makes chunked Promise.all safe — only the main loop touches the
 * shared `updated` array.
 */
const processOneTrade = async (trade, { fetchBars, emaPeriods, htfTimeframes }) => {
  let bars = null;
  let htfBars = null;
  try {
    // Always fetch 1m bars for the MAE/MFE walk. NT writes 1m natively
    // (so this is no extra cost for NT-sourced data), and at 1m the walk
    // has fine enough resolution to handle short-duration trades (e.g.
    // 3-minute stop-outs) that were systematically miscalculated when
    // analysis ran against 5m bars.
    //
    // HTF bars (15m/1h/4h by default) are still fetched at their natural
    // timeframes — they're snapshot-only (EMA stack at entry), so single-bar
    // resolution isn't required.
    const ANALYSIS_TF = '1m';
    const fetchTfs = [ANALYSIS_TF, ...(Array.isArray(htfTimeframes) ? htfTimeframes : [])];
    const fetches = await Promise.all(fetchTfs.map(tf =>
      fetchBars({ symbol: trade.symbol, dateStr: trade.date, timeframe: tf })
        .then(res => ({ tf, bars: Array.isArray(res?.bars) ? res.bars : [] }))
        .catch(() => ({ tf, bars: [] }))
    ));
    const analysisFetch = fetches.find(f => f.tf === ANALYSIS_TF);
    if (analysisFetch && analysisFetch.bars.length > 0) bars = analysisFetch.bars;
    // HTF bundle — only include TFs that actually returned bars.
    const htfMap = {};
    for (const f of fetches) {
      if (f.tf === ANALYSIS_TF) continue;
      if (f.bars.length > 0) htfMap[f.tf] = f.bars;
    }
    if (Object.keys(htfMap).length > 0) htfBars = htfMap;
  } catch (e) {
    // Swallow — keep batch going. Per-trade fetch errors are logged by
    // BarDataService already; here we just count it as skipped.
  }

  if (!bars) {
    return {
      kind: 'no-bars',
      tradeId: trade.id,
      attempt: {
        attemptedAt: new Date().toISOString(),
        skippedReason: 'no-bars',
      },
    };
  }

  let analysis;
  let analysisError = null;
  try {
    analysis = computeMaeMfe(trade, bars, { emaPeriods, htfBars });
  } catch (e) {
    analysis = null;
    analysisError = e?.message || String(e);
  }

  if (!analysis) {
    // Log the swallow so we can diagnose root cause without losing the
    // catch-and-continue behavior. Each unique analyzer bug should fire
    // once per affected trade per backfill run — not exactly noisy, and
    // tractable to act on when something IS broken. Pre-empts the
    // "everything looks fine but pill keeps coming back at 5" mystery.
    // eslint-disable-next-line no-console
    console.warn(
      `[backfill] analysis failed for trade ${trade.id} (${trade.symbol} ${trade.date} ${trade.entryTime}):`,
      analysisError || 'computeMaeMfe returned null'
    );
    return {
      kind: 'analysis-failed',
      tradeId: trade.id,
      attempt: {
        attemptedAt: new Date().toISOString(),
        skippedReason: 'analysis-failed',
        error: analysisError || 'computeMaeMfe returned null',
      },
    };
  }

  // Phase 19 — detect in-trade counter-signals using the same bars we
  // already loaded. Cheap (one walk over htfBars; computeEmas is O(n) per
  // period), and the result lives on the same analysis blob so downstream
  // consumers (analyzers, trade card chips, live whispers) read from one
  // place. Errors are non-fatal — the rest of the analysis still ships.
  try {
    const counterSignals = detectInTradeCounterSignals(trade, { htfBars, emaPeriods });
    if (Array.isArray(counterSignals)) {
      analysis.counterSignals = counterSignals;
    }
  } catch (e) {
    // Don't kill the trade's analysis over a counter-signal detector bug
    // eslint-disable-next-line no-console
    console.warn(`[backfill] counterSignals detection failed for trade ${trade.id}:`, e?.message || e);
  }

  return {
    kind: 'computed',
    tradeId: trade.id,
    analysis,
  };
};

export const backfillBarAnalysis = async ({
  trades,
  fetchBars,
  // eslint-disable-next-line no-unused-vars
  timeframe: _ignoredTimeframe,  // retained for API compat; analysis is always 1m now
  emaPeriods,
  htfTimeframes = ['15m', '1h', '4h'],
  onProgress,
  signal,
  requireFields = 'missing',
  concurrency = DEFAULT_CHUNK_SIZE,
}) => {
  if (!Array.isArray(trades)) return { trades: [], processed: 0, computed: 0, skipped: 0, aborted: false };
  if (typeof fetchBars !== 'function') {
    throw new Error('backfillBarAnalysis: fetchBars is required');
  }

  // Work on a shallow copy so the input isn't mutated mid-batch
  const updated = trades.slice();
  const eligible = updated.filter(t => isEligible(t, requireFields));
  const total = eligible.length;
  let processed = 0;
  let computed = 0;
  let skipped = 0;
  let aborted = false;

  // Chunked parallel processing. Trades within a chunk run concurrently
  // via Promise.all (their I/O overlaps); chunks themselves run sequentially
  // so we can honor signal.aborted at a predictable cadence and so the
  // merge-into-updated step never has to deal with concurrent writers.
  const chunkSize = Math.max(1, concurrency | 0);
  for (let i = 0; i < eligible.length; i += chunkSize) {
    if (signal?.aborted) {
      aborted = true;
      break;
    }
    const chunk = eligible.slice(i, i + chunkSize);
    // All trades in the chunk fan out their fetches concurrently. The
    // OS file cache makes repeated reads of the same CSV cheap, so 8
    // trades on adjacent days share I/O work.
    const results = await Promise.all(chunk.map(t =>
      processOneTrade(t, { fetchBars, emaPeriods, htfTimeframes })
    ));

    // Apply mutations sequentially after the chunk settles. This keeps
    // the `updated` array mutation single-threaded (no race) while
    // preserving the per-trade onProgress firing order.
    //
    // Abort handling: the chunk's parallel fetches already completed by
    // the time we get here (sunk cost), but the user can still abort
    // BETWEEN per-result merges. That lets the abort take effect within
    // ~1 chunk of the click, instead of waiting for the entire run.
    // Without this mid-chunk check, an abort during result-1's
    // onProgress wouldn't stop the merge until the next chunk boundary,
    // which feels broken at chunkSize=8.
    for (const r of results) {
      processed++;
      const idx = updated.findIndex(t => t && t.id === r.tradeId);
      if (idx >= 0) {
        if (r.kind === 'computed') {
          const next = { ...updated[idx], barAnalysis: r.analysis };
          // If this trade was previously marked as "tried, no bars" and
          // now we successfully analyzed it (bars finally arrived), clear
          // the attempt marker so it's not confusingly stamped after success.
          if (next._barAnalysisAttempt) {
            delete next._barAnalysisAttempt;
          }
          // Backfill broker-style mae/mfe only when empty (broker imports win).
          if ((next.mae == null || next.mae === '') && r.analysis.maeDollars != null) {
            next.mae = r.analysis.maeDollars;
          }
          if ((next.mfe == null || next.mfe === '') && r.analysis.mfeDollars != null) {
            next.mfe = r.analysis.mfeDollars;
          }
          updated[idx] = next;
          computed++;
        } else {
          // Stamp a marker so countTradesNeedingBackfill stops counting
          // this trade on future runs. 'no-bars' = no CSV data for the
          // date; 'analysis-failed' = bars existed but computeMaeMfe
          // threw. Both stop the pill from nagging.
          updated[idx] = { ...updated[idx], _barAnalysisAttempt: r.attempt };
          skipped++;
        }
      }
      onProgress?.({ processed, total, computed, skipped, lastTradeId: r.tradeId });
      // After firing onProgress, check whether the callback (or anything
      // else) signaled abort. Break the merge loop early so the remaining
      // chunk results aren't merged. This matches the pre-parallelization
      // semantics where abort during onProgress stopped the next iteration.
      if (signal?.aborted) {
        aborted = true;
        break;
      }
    }
    if (aborted) break;
  }

  return { trades: updated, processed, computed, skipped, aborted };
};
